/**
 * Layout and drawing for the VR interface's panels (vrInterface.ts). A headset can't show page UI,
 * so every menu and popup is drawn onto a 2D canvas used as the texture of a quad in front of the
 * visitor. Canvas text (not the engine's text elements) because those need MSDF font atlases,
 * which don't carry the accents and scripts scenes are written in, and our lists are long.
 *
 * Layout is pure (rows, buttons and their hit regions in canvas pixels), so it is unit-tested;
 * painting only reads it. A controller ray's hit point on a panel, in canvas pixels, maps to the
 * region under it (panelHitAt).
 */

/** A clickable area of a panel, in canvas pixels. */
export interface HitRegion {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
}

/** The region under a canvas point (the last one listed wins: buttons sit above rows). */
export function panelHitAt(regions: readonly HitRegion[], x: number, y: number): HitRegion | null {
    for (let i = regions.length - 1; i >= 0; i--) {
        const r = regions[i];
        if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return r;
    }
    return null;
}

/** Colours of every panel: the scene's UI colour as the accent on the viewer's dark panels. */
export interface VrPanelTheme {
    accent: string;
    /** Text on the accent. */
    onAccent: string;
    background: string;
    /** Hovered rows and buttons. */
    hover: string;
    text: string;
    muted: string;
    divider: string;
}

const HEX = /^#([0-9a-f]{6})$/i;

/** Readable text colour on a #rrggbb background. */
function textOn(hex: string): string {
    const m = HEX.exec(hex);
    if (!m) return '#ffffff';
    const n = parseInt(m[1], 16);
    const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return lum > 0.6 ? '#141414' : '#ffffff';
}

export function vrPanelTheme(accent?: string | null): VrPanelTheme {
    const color = accent && HEX.test(accent) ? accent : '#cc5833';
    return {
        accent: color,
        onAccent: textOn(color),
        background: 'rgba(18, 18, 20, 0.94)',
        hover: 'rgba(255, 255, 255, 0.12)',
        text: '#f2f0ed',
        muted: 'rgba(242, 240, 237, 0.62)',
        divider: 'rgba(255, 255, 255, 0.14)',
    };
}

/** A tick box's state ('mixed': some of what it covers). */
export type VrCheck = 'on' | 'off' | 'mixed';

/** One row of a list panel. */
export interface VrListRow {
    /** What selecting the row does (the host's action id). */
    id: string;
    label: string;
    /** A second, smaller line. */
    sub?: string;
    /** Right-aligned: a count, "Now". */
    detail?: string;
    /** Indent level (folders). */
    depth?: number;
    /** A tick box (radio when `radio`). Selecting the row toggles it unless `checkId` is set. */
    check?: VrCheck;
    radio?: boolean;
    /** The tick box alone is its own region with this id (a folder: the row opens it, the box shows it). */
    checkId?: string;
    /** A colour swatch (a region layer). */
    swatch?: string;
    /** A folder's arrow. */
    expand?: 'open' | 'closed';
    /** Highlighted: the current tour stop. */
    active?: boolean;
    /** A section heading (not selectable); an empty label draws a divider. */
    heading?: boolean;
    disabled?: boolean;
}

/** A button in a panel's footer or header. */
export interface VrButton {
    id: string;
    label: string;
    primary?: boolean;
    disabled?: boolean;
}

/** Sizes of a list panel's parts, in canvas pixels. */
export interface VrListMetrics {
    header: number;
    row: number;
    heading: number;
    footer: number;
    pad: number;
    indent: number;
    font: number;
    subFont: number;
    titleFont: number;
}

/** The floating panels: about 1700 px per metre, so 34 px text is 2 cm tall (readable at a metre). */
export const VR_LIST_METRICS: VrListMetrics = { header: 104, row: 88, heading: 56, footer: 112, pad: 28, indent: 40, font: 34, subFont: 26, titleFont: 40 };
/** The wrist menu, held closer: smaller pixels per row, same angular size. */
export const VR_WRIST_METRICS: VrListMetrics = { header: 72, row: 76, heading: 40, footer: 0, pad: 20, indent: 24, font: 30, subFont: 22, titleFont: 28 };

export interface VrListPanelSpec {
    title: string;
    rows: readonly VrListRow[];
    /** Shown when there are no rows. */
    empty?: string;
    /** Footer buttons (e.g. Previous / Next). */
    footer?: readonly VrButton[];
    /** Header close (×) and drag handle. Default true. */
    closable?: boolean;
    /** List scroll in pixels (clamped by the layout). */
    scroll?: number;
}

export interface LaidOutRow {
    row: VrListRow;
    /** Top of the row in canvas pixels, after scrolling. */
    y: number;
    h: number;
}

export interface VrListPanelLayout {
    width: number;
    height: number;
    metrics: VrListMetrics;
    /** The list's viewport. */
    list: { y: number; h: number };
    /** Rows that intersect the viewport. */
    rows: LaidOutRow[];
    contentHeight: number;
    scroll: number;
    footerButtons: Array<VrButton & { x: number; y: number; w: number; h: number }>;
    regions: HitRegion[];
}

const rowHeight = (row: VrListRow, m: VrListMetrics) => (row.heading ? m.heading : row.sub ? m.row + m.subFont : m.row);

/** The scroll clamped to the content: 0 at the top, never past the end. */
export function clampScroll(scroll: number, contentHeight: number, viewportHeight: number): number {
    const max = Math.max(0, contentHeight - viewportHeight);
    return Number.isFinite(scroll) ? Math.min(max, Math.max(0, scroll)) : 0;
}

/** Lay out a list panel: header (title, ×, drag handle), scrolled rows, footer buttons, and the regions. */
export function layoutVrListPanel(spec: VrListPanelSpec, size: { width: number; height: number }, metrics: VrListMetrics = VR_LIST_METRICS): VrListPanelLayout {
    const { width, height } = size;
    const m = metrics;
    const regions: HitRegion[] = [];
    const closable = spec.closable !== false;
    const footer = spec.footer?.length ? m.footer : 0;
    const list = { y: m.header, h: height - m.header - footer };
    let contentHeight = 0;
    for (const row of spec.rows) contentHeight += rowHeight(row, m);
    const scroll = clampScroll(spec.scroll ?? 0, contentHeight, list.h);
    // The list first: the header and footer regions come after, so they win where they overlap.
    regions.push({ id: '__list', x: 0, y: list.y, w: width, h: list.h });
    const rows: LaidOutRow[] = [];
    let y = list.y - scroll;
    for (const row of spec.rows) {
        const h = rowHeight(row, m);
        if (y + h > list.y && y < list.y + list.h) {
            rows.push({ row, y, h });
            if (!row.heading && !row.disabled) {
                // Rows are clipped to the viewport for hits too.
                const top = Math.max(y, list.y), bottom = Math.min(y + h, list.y + list.h);
                regions.push({ id: row.id, x: 0, y: top, w: width, h: bottom - top });
                if (row.checkId && row.check) {
                    const box = checkBoxRect(row, y, h, width, m);
                    if (box.y + box.h > list.y && box.y < list.y + list.h) regions.push({ id: row.checkId, ...box });
                }
            }
        }
        y += h;
    }
    if (closable) {
        // The header drags the panel; × closes it.
        regions.push({ id: '__drag', x: 0, y: 0, w: width, h: m.header });
        regions.push({ id: '__close', x: width - m.header, y: 0, w: m.header, h: m.header });
    }
    const footerButtons: VrListPanelLayout['footerButtons'] = [];
    if (footer && spec.footer) {
        const gap = 16;
        const n = spec.footer.length;
        const w = (width - m.pad * 2 - gap * (n - 1)) / n;
        spec.footer.forEach((button, i) => {
            const rect = { x: m.pad + i * (w + gap), y: height - footer + 18, w, h: footer - 36 };
            footerButtons.push({ ...button, ...rect });
            if (!button.disabled) regions.push({ id: button.id, ...rect });
        });
    }
    return { width, height, metrics: m, list, rows, contentHeight, scroll, footerButtons, regions };
}

/** Where a row's tick box sits (at the right, after any detail text's room is taken by the row). */
export function checkBoxRect(row: VrListRow, y: number, h: number, width: number, m: VrListMetrics): { x: number; y: number; w: number; h: number } {
    const size = Math.round(m.row * 0.5);
    // A tick box that is its own region gets a wider target around it.
    const target = row.checkId ? m.row : size;
    return { x: width - m.pad - target, y: y + (h - target) / 2, w: target, h: target };
}

/** The scroll that keeps rows [y, y + h) of the content in view. */
export function scrollToShow(scroll: number, y: number, h: number, viewportHeight: number): number {
    if (y < scroll) return y;
    if (y + h > scroll + viewportHeight) return y + h - viewportHeight;
    return scroll;
}

type Ctx = CanvasRenderingContext2D;

const FONT = '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
}

/** `text` cut with an ellipsis to fit `maxWidth` at the context's current font. */
export function fitText(measure: (s: string) => number, text: string, maxWidth: number): string {
    if (measure(text) <= maxWidth) return text;
    let lo = 0, hi = text.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (measure(`${text.slice(0, mid).trimEnd()}…`) <= maxWidth) lo = mid;
        else hi = mid - 1;
    }
    return `${text.slice(0, lo).trimEnd()}…`;
}

/** The panel's background (rounded) and header. */
function paintFrame(ctx: Ctx, width: number, height: number, title: string, m: VrListMetrics, theme: VrPanelTheme, closable: boolean, hoverId: string | null): void {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = theme.background;
    roundRect(ctx, 0, 0, width, height, 28);
    ctx.fill();
    ctx.strokeStyle = theme.divider;
    ctx.lineWidth = 3;
    roundRect(ctx, 1.5, 1.5, width - 3, height - 3, 27);
    ctx.stroke();
    // Header
    ctx.fillStyle = theme.accent;
    ctx.fillRect(m.pad, m.header - 4, 64, 4);
    ctx.fillStyle = theme.text;
    ctx.font = `600 ${m.titleFont}px ${FONT}`;
    ctx.textBaseline = 'middle';
    const titleRoom = width - m.pad * 2 - (closable ? m.header : 0);
    ctx.fillText(fitText((s) => ctx.measureText(s).width, title, titleRoom), m.pad, m.header / 2);
    if (closable) {
        const x = width - m.header, size = m.header;
        if (hoverId === '__close') {
            ctx.fillStyle = theme.hover;
            roundRect(ctx, x + 12, 12, size - 24, size - 24, 16);
            ctx.fill();
        }
        ctx.strokeStyle = theme.text;
        ctx.lineWidth = 4;
        const c = size / 2, s = size * 0.16;
        ctx.beginPath();
        ctx.moveTo(x + c - s, c - s);
        ctx.lineTo(x + c + s, c + s);
        ctx.moveTo(x + c + s, c - s);
        ctx.lineTo(x + c - s, c + s);
        ctx.stroke();
    }
    ctx.fillStyle = theme.divider;
    ctx.fillRect(0, m.header, width, 2);
}

function paintCheck(ctx: Ctx, rect: { x: number; y: number; w: number; h: number }, row: VrListRow, theme: VrPanelTheme, m: VrListMetrics): void {
    const size = Math.round(m.row * 0.5);
    const x = rect.x + (rect.w - size) / 2, y = rect.y + (rect.h - size) / 2;
    const on = row.check === 'on' || row.check === 'mixed';
    ctx.lineWidth = 4;
    if (row.radio) {
        ctx.beginPath();
        ctx.arc(x + size / 2, y + size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.strokeStyle = on ? theme.accent : theme.muted;
        ctx.stroke();
        if (on) {
            ctx.beginPath();
            ctx.arc(x + size / 2, y + size / 2, size / 4, 0, Math.PI * 2);
            ctx.fillStyle = theme.accent;
            ctx.fill();
        }
        return;
    }
    roundRect(ctx, x, y, size, size, 8);
    if (on) {
        ctx.fillStyle = theme.accent;
        ctx.fill();
        ctx.strokeStyle = theme.onAccent;
        ctx.beginPath();
        if (row.check === 'mixed') {
            ctx.moveTo(x + size * 0.25, y + size / 2);
            ctx.lineTo(x + size * 0.75, y + size / 2);
        } else {
            ctx.moveTo(x + size * 0.24, y + size * 0.52);
            ctx.lineTo(x + size * 0.43, y + size * 0.72);
            ctx.lineTo(x + size * 0.78, y + size * 0.3);
        }
        ctx.stroke();
    } else {
        ctx.strokeStyle = theme.muted;
        ctx.stroke();
    }
}

function paintButton(ctx: Ctx, b: VrButton & { x: number; y: number; w: number; h: number }, theme: VrPanelTheme, hovered: boolean, font: number): void {
    roundRect(ctx, b.x, b.y, b.w, b.h, 18);
    ctx.globalAlpha = b.disabled ? 0.4 : 1;
    if (b.primary) {
        ctx.fillStyle = theme.accent;
        ctx.fill();
        if (hovered) {
            ctx.fillStyle = 'rgba(255,255,255,0.15)';
            ctx.fill();
        }
    } else {
        ctx.fillStyle = hovered ? theme.hover : 'rgba(255,255,255,0.06)';
        ctx.fill();
        ctx.strokeStyle = theme.divider;
        ctx.lineWidth = 3;
        ctx.stroke();
    }
    ctx.fillStyle = b.primary ? theme.onAccent : theme.text;
    ctx.font = `600 ${font}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(fitText((s) => ctx.measureText(s).width, b.label, b.w - 24), b.x + b.w / 2, b.y + b.h / 2);
    ctx.textAlign = 'left';
    ctx.globalAlpha = 1;
}

/** Paint a laid-out list panel (`hoverId`: the region a ray points at). */
export function paintVrListPanel(ctx: Ctx, layout: VrListPanelLayout, spec: VrListPanelSpec, theme: VrPanelTheme, hoverId: string | null): void {
    const { width, height, metrics: m, list } = layout;
    paintFrame(ctx, width, height, spec.title, m, theme, spec.closable !== false, hoverId);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, list.y, width, list.h);
    ctx.clip();
    if (!spec.rows.length && spec.empty) {
        ctx.fillStyle = theme.muted;
        ctx.font = `italic ${m.subFont + 4}px ${FONT}`;
        ctx.textBaseline = 'middle';
        ctx.fillText(fitText((s) => ctx.measureText(s).width, spec.empty, width - m.pad * 2), m.pad, list.y + m.row / 2);
    }
    for (const { row, y, h } of layout.rows) {
        if (row.heading) {
            if (!row.label) {
                ctx.fillStyle = theme.divider;
                ctx.fillRect(m.pad, y + h / 2 - 1, width - m.pad * 2, 2);
                continue;
            }
            ctx.fillStyle = theme.muted;
            ctx.font = `600 ${m.subFont}px ${FONT}`;
            ctx.textBaseline = 'middle';
            ctx.fillText(fitText((s) => ctx.measureText(s).width, row.label.toUpperCase(), width - m.pad * 2), m.pad, y + h / 2 + 6);
            continue;
        }
        const hovered = hoverId === row.id || (row.checkId !== undefined && hoverId === row.checkId);
        if (row.active || hovered) {
            ctx.fillStyle = row.active ? 'rgba(255,255,255,0.08)' : theme.hover;
            roundRect(ctx, 10, y + 4, width - 20, h - 8, 16);
            ctx.fill();
            if (row.active) {
                ctx.fillStyle = theme.accent;
                ctx.fillRect(10, y + 14, 6, h - 28);
            }
        }
        ctx.globalAlpha = row.disabled ? 0.45 : 1;
        let x = m.pad + (row.depth ?? 0) * m.indent;
        const midY = row.sub ? y + m.row / 2 - 6 : y + h / 2;
        if (row.expand) {
            ctx.fillStyle = theme.muted;
            ctx.beginPath();
            const s = m.font * 0.32;
            if (row.expand === 'open') {
                ctx.moveTo(x, midY - s / 2);
                ctx.lineTo(x + s * 2, midY - s / 2);
                ctx.lineTo(x + s, midY + s * 0.8);
            } else {
                ctx.moveTo(x + s * 0.3, midY - s);
                ctx.lineTo(x + s * 1.6, midY);
                ctx.lineTo(x + s * 0.3, midY + s);
            }
            ctx.closePath();
            ctx.fill();
            x += m.font * 0.9;
        }
        if (row.swatch) {
            ctx.fillStyle = row.swatch;
            ctx.beginPath();
            ctx.arc(x + m.font * 0.3, midY, m.font * 0.3, 0, Math.PI * 2);
            ctx.fill();
            x += m.font * 0.9;
        }
        // Right side: tick box, then detail text to its left.
        let right = width - m.pad;
        if (row.check) {
            const box = checkBoxRect(row, y, row.sub ? m.row : h, width, m);
            paintCheck(ctx, box, row, theme, m);
            right = box.x - 12;
        }
        if (row.detail) {
            ctx.font = `${m.subFont}px ${FONT}`;
            ctx.fillStyle = theme.muted;
            ctx.textBaseline = 'middle';
            const w = ctx.measureText(row.detail).width;
            ctx.fillText(row.detail, right - w, midY);
            right -= w + 18;
        }
        ctx.fillStyle = theme.text;
        ctx.font = `${row.expand ? '600 ' : ''}${m.font}px ${FONT}`;
        ctx.textBaseline = 'middle';
        ctx.fillText(fitText((s) => ctx.measureText(s).width, row.label, right - x), x, midY);
        if (row.sub) {
            ctx.fillStyle = theme.muted;
            ctx.font = `${m.subFont}px ${FONT}`;
            ctx.fillText(fitText((s) => ctx.measureText(s).width, row.sub, right - x), x, midY + m.font * 0.95);
        }
        ctx.globalAlpha = 1;
    }
    ctx.restore();
    // A scroll bar when the list overflows.
    if (layout.contentHeight > list.h) {
        const trackH = list.h - 24;
        const barH = Math.max(40, (list.h / layout.contentHeight) * trackH);
        const barY = list.y + 12 + (layout.scroll / (layout.contentHeight - list.h)) * (trackH - barH);
        ctx.fillStyle = theme.divider;
        roundRect(ctx, width - 12, barY, 6, barH, 3);
        ctx.fill();
    }
    if (layout.footerButtons.length) {
        ctx.fillStyle = theme.divider;
        ctx.fillRect(0, height - m.footer, width, 2);
        for (const b of layout.footerButtons) paintButton(ctx, b, theme, hoverId === b.id, m.font - 2);
    }
}

// ── The popup panel: a hotspot's (or model's, or overlay's) content ───────────────────────────

/** What the popup panel shows besides text: a photo of a gallery, a video, or a PDF page. */
export type VrMediaKind = 'image' | 'video' | 'pdf';

export interface VrPopupSpec {
    title: string;
    text: string;
    /** The media tabs the popup has, in order (one tab: no tab bar). */
    media: readonly VrMediaKind[];
    current: VrMediaKind | null;
    /** "3 / 7" paging for the gallery or PDF; null hides the arrows. */
    page: { index: number; count: number } | null;
    /** Video: playing or paused (null: no video showing). */
    playing: boolean | null;
    /** A saveable link (web page, external link): the "Open after VR" button. */
    link: { label: string; saved: boolean } | null;
    /** A line naming what can't show in VR at all (a 3D model). */
    note?: string;
    /** Text scroll in pixels. */
    scroll?: number;
    closable?: boolean;
    background?: string;
    color?: string;
}

export interface VrPopupLayout {
    width: number;
    height: number;
    /** Where media draws (the video quad covers it). Null: no media. */
    mediaRect: { x: number; y: number; w: number; h: number } | null;
    textRect: { x: number; y: number; w: number; h: number };
    buttons: Array<VrButton & { x: number; y: number; w: number; h: number }>;
    tabs: Array<VrButton & { x: number; y: number; w: number; h: number }>;
    scroll: number;
    regions: HitRegion[];
}

export const VR_POPUP_PIXELS = { width: 1280, height: 800 } as const;
export const VR_POPUP_METRES = { width: 0.96, height: 0.6 } as const;
const POPUP_HEADER = 104;
const POPUP_FOOTER = 112;
const POPUP_PAD = 32;

const MEDIA_LABELS: Record<VrMediaKind, string> = { image: 'Photos', video: 'Video', pdf: 'PDF' };

/** Lay out the popup panel: header, tabs, media on the left, text on the right, controls below. */
export function layoutVrPopup(spec: VrPopupSpec, size: { width: number; height: number } = VR_POPUP_PIXELS, textHeight = 0): VrPopupLayout {
    const { width, height } = size;
    const regions: HitRegion[] = [];
    const tabsH = spec.media.length > 1 ? 72 : 0;
    const bodyY = POPUP_HEADER + tabsH;
    const bodyH = height - bodyY - POPUP_FOOTER;
    const hasMedia = spec.current !== null;
    const mediaW = hasMedia ? Math.round(width * 0.58) : 0;
    const mediaRect = hasMedia ? { x: POPUP_PAD, y: bodyY + 16, w: mediaW - POPUP_PAD, h: bodyH - 32 } : null;
    const textX = hasMedia ? mediaW + POPUP_PAD : POPUP_PAD;
    const textRect = { x: textX, y: bodyY + 16, w: width - textX - POPUP_PAD, h: bodyH - 32 };
    const scroll = clampScroll(spec.scroll ?? 0, textHeight, textRect.h);
    regions.push({ id: '__text', ...textRect });
    if (mediaRect) regions.push({ id: '__media', ...mediaRect });
    const tabs: VrPopupLayout['tabs'] = [];
    if (tabsH) {
        let x = POPUP_PAD;
        for (const kind of spec.media) {
            const w = 180;
            const tab = { id: `tab:${kind}`, label: MEDIA_LABELS[kind], primary: spec.current === kind, x, y: POPUP_HEADER + 12, w, h: tabsH - 20 };
            tabs.push(tab);
            regions.push({ id: tab.id, x: tab.x, y: tab.y, w: tab.w, h: tab.h });
            x += w + 14;
        }
    }
    // Footer: paging / play controls on the left, the link button on the right.
    const buttons: VrPopupLayout['buttons'] = [];
    const by = height - POPUP_FOOTER + 18, bh = POPUP_FOOTER - 36;
    let x = POPUP_PAD;
    if (spec.page && spec.page.count > 1) {
        buttons.push({ id: 'prev', label: '‹', disabled: spec.page.index <= 0, x, y: by, w: 110, h: bh });
        x += 110 + 12 + 150 + 12;
        buttons.push({ id: 'next', label: '›', disabled: spec.page.index >= spec.page.count - 1, x: x - 0, y: by, w: 110, h: bh });
        x += 110 + 16;
    }
    if (spec.playing !== null) {
        buttons.push({ id: 'play', label: spec.playing ? 'Pause' : 'Play', primary: !spec.playing, x, y: by, w: 200, h: bh });
        x += 216;
    }
    if (spec.link) {
        const w = 360;
        buttons.push({ id: 'save-link', label: spec.link.saved ? 'Saved for later ✓' : spec.link.label, primary: !spec.link.saved, disabled: spec.link.saved, x: width - POPUP_PAD - w, y: by, w, h: bh });
    }
    for (const b of buttons) if (!b.disabled) regions.push({ id: b.id, x: b.x, y: b.y, w: b.w, h: b.h });
    if (spec.closable !== false) {
        regions.push({ id: '__drag', x: 0, y: 0, w: width, h: POPUP_HEADER });
        regions.push({ id: '__close', x: width - POPUP_HEADER, y: 0, w: POPUP_HEADER, h: POPUP_HEADER });
    }
    return { width, height, mediaRect, textRect, buttons, tabs, scroll, regions };
}

/** Words of `text` broken into lines no wider than `maxWidth` (explicit line breaks kept). */
export function wrapLines(measure: (s: string) => number, text: string, maxWidth: number): string[] {
    const lines: string[] = [];
    for (const paragraph of text.split('\n')) {
        let line = '';
        for (const word of paragraph.split(/\s+/).filter(Boolean)) {
            const next = line ? `${line} ${word}` : word;
            if (line && measure(next) > maxWidth) {
                lines.push(line);
                line = word;
            } else {
                line = next;
            }
        }
        lines.push(line);
    }
    return lines;
}

export const VR_POPUP_TEXT = { font: 34, line: 46 } as const;

/** The height the popup's text takes at its width (for scrolling). */
export function popupTextHeight(measure: (s: string) => number, text: string, width: number): number {
    return text ? wrapLines(measure, text, width).length * VR_POPUP_TEXT.line : 0;
}

/**
 * Paint the popup. `media` is what the media area shows now (a photo or a rendered PDF page);
 * a video draws on its own quad over the media area, so the area is left dark for it.
 */
export function paintVrPopup(
    ctx: Ctx,
    layout: VrPopupLayout,
    spec: VrPopupSpec,
    theme: VrPanelTheme,
    hoverId: string | null,
    media: (CanvasImageSource & { width: number; height: number }) | null,
    mediaMessage: string | null,
): void {
    const { width, height } = layout;
    const m = { ...VR_LIST_METRICS, header: POPUP_HEADER };
    const panelTheme = { ...theme, background: spec.background || theme.background, text: spec.color || theme.text };
    paintFrame(ctx, width, height, spec.title, m, panelTheme, spec.closable !== false, hoverId);
    for (const tab of layout.tabs) paintButton(ctx, tab, panelTheme, hoverId === tab.id, 28);
    if (layout.mediaRect) {
        const r = layout.mediaRect;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        roundRect(ctx, r.x, r.y, r.w, r.h, 16);
        ctx.fill();
        if (media && media.width > 0 && media.height > 0) {
            const scale = Math.min(r.w / media.width, r.h / media.height);
            const w = media.width * scale, h = media.height * scale;
            ctx.drawImage(media, r.x + (r.w - w) / 2, r.y + (r.h - h) / 2, w, h);
        } else if (mediaMessage) {
            ctx.fillStyle = panelTheme.muted;
            ctx.font = `${VR_LIST_METRICS.subFont + 2}px ${FONT}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(fitText((s) => ctx.measureText(s).width, mediaMessage, r.w - 40), r.x + r.w / 2, r.y + r.h / 2);
            ctx.textAlign = 'left';
        }
    }
    // Text, scrolled and clipped.
    const t = layout.textRect;
    ctx.save();
    ctx.beginPath();
    ctx.rect(t.x, t.y, t.w, t.h);
    ctx.clip();
    ctx.fillStyle = panelTheme.text;
    ctx.font = `${VR_POPUP_TEXT.font}px ${FONT}`;
    ctx.textBaseline = 'top';
    let y = t.y - layout.scroll;
    const body = [spec.text, spec.note ? `\n${spec.note}` : ''].filter(Boolean).join('\n');
    for (const line of wrapLines((s) => ctx.measureText(s).width, body, t.w)) {
        if (y + VR_POPUP_TEXT.line > t.y && y < t.y + t.h) ctx.fillText(line, t.x, y);
        y += VR_POPUP_TEXT.line;
    }
    ctx.restore();
    ctx.fillStyle = panelTheme.divider;
    ctx.fillRect(0, height - POPUP_FOOTER, width, 2);
    for (const b of layout.buttons) paintButton(ctx, b, panelTheme, hoverId === b.id, b.label.length <= 2 ? 52 : 30);
    if (spec.page && spec.page.count > 1) {
        const prev = layout.buttons.find((b) => b.id === 'prev');
        if (prev) {
            ctx.fillStyle = panelTheme.muted;
            ctx.font = `${VR_LIST_METRICS.subFont + 2}px ${FONT}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${spec.page.index + 1} / ${spec.page.count}`, prev.x + prev.w + 12 + 75, prev.y + prev.h / 2);
            ctx.textAlign = 'left';
        }
    }
}

// ── The banner (a waypoint's name, a top overlay) and labels (measurements) ───────────────────

export const VR_BANNER_PIXELS = { width: 1280, height: 200 } as const;
export const VR_BANNER_METRES = { width: 0.8, height: 0.125 } as const;

export function paintVrBanner(ctx: Ctx, title: string, subtitle: string, theme: VrPanelTheme): void {
    const { width, height } = VR_BANNER_PIXELS;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(14, 14, 16, 0.86)';
    roundRect(ctx, 0, 0, width, height, 36);
    ctx.fill();
    ctx.fillStyle = theme.accent;
    ctx.fillRect(48, 34, 8, height - 68);
    ctx.fillStyle = theme.text;
    ctx.textBaseline = 'middle';
    const textX = 84, room = width - textX - 48;
    if (title) {
        ctx.font = `600 ${subtitle ? 54 : 62}px ${FONT}`;
        ctx.fillText(fitText((s) => ctx.measureText(s).width, title, room), textX, subtitle ? height * 0.36 : height / 2);
    }
    if (subtitle) {
        ctx.fillStyle = theme.muted;
        ctx.font = `${title ? 38 : 48}px ${FONT}`;
        ctx.fillText(fitText((s) => ctx.measureText(s).width, subtitle, room), textX, title ? height * 0.7 : height / 2);
    }
}

export const VR_LABEL_PIXELS = { width: 384, height: 96 } as const;

/** A measurement's label: its value in a pill (the measurement colour as the edge). */
export function paintVrLabel(ctx: Ctx, text: string, color: string): void {
    const { width, height } = VR_LABEL_PIXELS;
    ctx.clearRect(0, 0, width, height);
    ctx.font = `600 52px ${FONT}`;
    const w = Math.min(width - 8, ctx.measureText(text).width + 56);
    const x = (width - w) / 2;
    ctx.fillStyle = 'rgba(12, 12, 14, 0.85)';
    roundRect(ctx, x, 6, w, height - 12, (height - 12) / 2);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(fitText((s) => ctx.measureText(s).width, text, w - 40), width / 2, height / 2 + 2);
    ctx.textAlign = 'left';
}

// ── The keyboard (the directory's search) ─────────────────────────────────────────────────────

export const VR_KEYBOARD_ROWS: readonly (readonly string[])[] = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    ['w', 'x', 'c', 'v', 'b', 'n', "'", '-', '⌫'],
    ['space', 'clear'],
];

/** The keyboard's keys under a list panel's search line: regions `key:<char>` / `key:space` / `key:back` / `key:clear`. */
export function layoutVrKeyboard(area: { x: number; y: number; w: number; h: number }): Array<{ id: string; label: string; x: number; y: number; w: number; h: number }> {
    const keys: Array<{ id: string; label: string; x: number; y: number; w: number; h: number }> = [];
    const rows = VR_KEYBOARD_ROWS.length;
    const gap = 8;
    const keyH = (area.h - gap * (rows - 1)) / rows;
    VR_KEYBOARD_ROWS.forEach((row, r) => {
        const units = row.reduce((n, k) => n + (k === 'space' ? 6 : k === 'clear' ? 3 : 1), 0);
        const unitW = (area.w - gap * (row.length - 1)) / Math.max(10, units);
        let x = area.x + (area.w - (unitW * units + gap * (row.length - 1))) / 2;
        for (const k of row) {
            const w = unitW * (k === 'space' ? 6 : k === 'clear' ? 3 : 1);
            const id = k === '⌫' ? 'key:back' : k === 'space' ? 'key:space' : k === 'clear' ? 'key:clear' : `key:${k}`;
            const label = k === 'space' ? '␣' : k === 'clear' ? '✕ Clear' : k;
            keys.push({ id, label, x, y: area.y + r * (keyH + gap), w, h: keyH });
            x += w + gap;
        }
    });
    return keys;
}

/** Apply a keyboard key to the search text. */
export function applyVrKey(text: string, keyId: string): string {
    if (keyId === 'key:back') return text.slice(0, -1);
    if (keyId === 'key:clear') return '';
    if (keyId === 'key:space') return text.endsWith(' ') || !text ? text : `${text} `;
    const ch = keyId.startsWith('key:') ? keyId.slice(4) : '';
    return ch.length === 1 ? `${text}${ch}`.slice(0, 40) : text;
}

/** Paint the keyboard panel (`size`: its canvas) and its keys. */
export function paintVrKeyboard(ctx: Ctx, size: { width: number; height: number }, keys: ReturnType<typeof layoutVrKeyboard>, theme: VrPanelTheme, hoverId: string | null): void {
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = theme.background;
    roundRect(ctx, 0, 0, size.width, size.height, 24);
    ctx.fill();
    for (const k of keys) {
        paintButton(ctx, { ...k, label: k.label }, theme, hoverId === k.id, k.label.length > 2 ? 26 : 34);
    }
}
